import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function useQuery() {
  return new URLSearchParams(window.location.search);
}

export const urlRegex =
  /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

export function isUrl(url: string) {
  return urlRegex.test(url);
}

export const formatIncome = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) return '';
  return '$' + num.toLocaleString('en-US');
};

export const convertMarkdownToHtml = (markdown: string): string => {
  const applyInlineFormatting = (text: string): string => {
    // Жирный текст: **text** -> <strong>text</strong>
    let result = text.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
    // Курсив: *text* -> <em>text</em>
    result = result.replace(/(?<!\*)\*([^\*]+)\*(?!\*)/g, '<em>$1</em>');
    return result;
  };

  // Удаляем кодовые блоки ``` и слово "markdown" как раньше
  let input = markdown.replaceAll('```', '').replace('markdown', '');

  const lines = input.split(/\r?\n/);
  const htmlLines: string[] = [];

  let openListType: 'ul' | 'ol' | null = null;
  const closeOpenListIfAny = () => {
    if (openListType) {
      htmlLines.push(openListType === 'ul' ? '</ul>' : '</ol>');
      openListType = null;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    // Заголовки: #, ##, ### -> <h3>, <h4>, <h5>
    const h3 = /^###\s+(.+)$/.exec(line);
    const h2 = /^##\s+(.+)$/.exec(line);
    const h1 = /^#\s+(.+)$/.exec(line);
    if (h3 || h2 || h1) {
      closeOpenListIfAny();
      if (h3) {
        htmlLines.push(`<h5>${applyInlineFormatting(h3[1])}</h5>`);
      } else if (h2) {
        htmlLines.push(`<h4>${applyInlineFormatting(h2[1])}</h4>`);
      } else if (h1) {
        htmlLines.push(`<h3>${applyInlineFormatting(h1[1])}</h3>`);
      }
      continue;
    }

    // Ненумерованные списки: - item
    const ulMatch = /^-\s+(.+)$/.exec(line);
    if (ulMatch) {
      if (openListType !== 'ul') {
        closeOpenListIfAny();
        htmlLines.push('<ul>');
        openListType = 'ul';
      }
      htmlLines.push(`<li>${applyInlineFormatting(ulMatch[1])}</li>`);
      continue;
    }

    // Нумерованные списки: 1. item, 2. item, ...
    const olMatch = /^\d+\.\s+(.+)$/.exec(line);
    if (olMatch) {
      if (openListType !== 'ol') {
        closeOpenListIfAny();
        htmlLines.push('<ol>');
        openListType = 'ol';
      }
      htmlLines.push(`<li>${applyInlineFormatting(olMatch[1])}</li>`);
      continue;
    }

    // Пустая строка — закрываем список при необходимости и просто пропускаем/оставляем
    if (/^\s*$/.test(line)) {
      closeOpenListIfAny();
      htmlLines.push('');
      continue;
    }

    // Обычная строка — закрываем список и добавляем как есть с инлайновым форматированием
    closeOpenListIfAny();
    htmlLines.push(applyInlineFormatting(line));
  }

  // Закрываем любой открытый список в конце
  if (openListType) {
    htmlLines.push(openListType === 'ul' ? '</ul>' : '</ol>');
  }

  return htmlLines.join('\n').replaceAll('\n\n', '\n');
};
